// Toast.js
import React, { createContext, useContext, useState, useCallback } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { IoClose } from "react-icons/io5";
import { FaCheckCircle } from "react-icons/fa";
import { CiCircleInfo } from "react-icons/ci";
import { FiAlertTriangle, FiAlertOctagon } from "react-icons/fi";
import styled, { css } from "styled-components";

const ToastContext = createContext();

// ---------------- Styled Components ----------------
const ToastContainer = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 9999;
`;

const ToastBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 300px;
  padding: 12px 16px;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: translateY(-20px);
  transition: all 0.3s ease;

  &.toast-enter-active {
    opacity: 1;
    transform: translateY(0);
  }
  &.toast-exit-active {
    opacity: 0;
    transform: translateY(-20px);
  }

  ${(props) =>
    props.type === "success" &&
    css`
      background-color: #16a34a;
    `}
  ${(props) =>
    props.type === "error" &&
    css`
      background-color: #dc2626;
    `}
  ${(props) =>
    props.type === "warning" &&
    css`
      background-color: #f59e0b;
    `}
  ${(props) =>
    props.type === "info" &&
    css`
      background-color: #2563eb;
    `}
`;

const CloseButton = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
`;

// ---------------- Toast Provider ----------------
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [time, setTime] = useState(500);

  const addToast = useCallback((message, type = "info", duration = 3000) => {
    const id = Date.now();
    setTime(duration);
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastContainer>
        <TransitionGroup>
          {toasts.map((toast) => (
            <CSSTransition
              key={toast.id}
              timeout={time}
              classNames="toast"
            >
              <ToastBox type={toast.type}>
                {toast.type === "success" && <FaCheckCircle size={20} />}
                {toast.type === "error" && <FiAlertOctagon size={20} />}
                {toast.type === "warning" && <FiAlertTriangle size={20} />}
                {toast.type === "info" && <CiCircleInfo size={20} />}
                <span>{toast.message}</span>
                <CloseButton onClick={() => removeToast(toast.id)}>
                  <IoClose size={16} />
                </CloseButton>
              </ToastBox>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </ToastContainer>
    </ToastContext.Provider>
  );
};


export const useToast = () => useContext(ToastContext);


export const withToast = (Component) => {
  return function ToastedComponent(props) {
    return (
      <ToastContext.Consumer>
        {(value) => <Component {...props} addToast={value.addToast} />}
      </ToastContext.Consumer>
    );
  };
};